// lib/coupons/alcoholExclusion.ts
//
// A1b (TABC): los cupones nunca rebajan líneas de alcohol. Helpers PUROS y
// seguros para el cliente: el carrito de la caja, el kiosko y la tienda online
// los usan igual que el servidor, para que lo que el cliente ve y lo que se cobra
// salgan de la MISMA cuenta.
//
// 🔴 No importar lib/promotions/alcohol.ts desde acá: arrastra date-fns-tz y el
// día operativo (lib/finance/businessDay), que no tienen nada que hacer en el
// bundle del cliente. Este archivo solo depende de lib/promotions/eligibleBase.ts
// (puro, sin más dependencias que round2 de lib/retail/pricing.ts).

import { discountBases } from "@/lib/promotions/eligibleBase"

/**
 * Qué cuenta como alcohol para el cupón, a nivel LOCAL ENTERO (no por carrito:
 * una cerveza agregada después de aplicar el cupón tiene que quedar fuera
 * también en el cliente).
 *
 * - `alcoholItemIds`: ítems con `items.is_alcohol = true`.
 * - `alcoholOptionIds`: opciones de modificador cuyo `counts_as_item_id` apunta a
 *   uno de esos ítems (p. ej. el combo con opción cerveza). Incluye opciones de
 *   quitar (`is_removal`): ante la duda, fuera del cupón (dirección segura TABC).
 */
export type CouponAlcoholFacts = {
  alcoholItemIds: readonly string[]
  alcoholOptionIds: readonly string[]
}

/** Sin alcohol marcado (hoy, todos los locales). Congelado: se comparte. */
export const EMPTY_COUPON_ALCOHOL_FACTS: CouponAlcoholFacts = Object.freeze({
  alcoholItemIds: Object.freeze([]) as readonly string[],
  alcoholOptionIds: Object.freeze([]) as readonly string[],
})

/**
 * ¿Esta línea es alcohol para el cupón? Sí si el ítem lo es o si ALGUNA de sus
 * opciones cuenta como un ítem de alcohol. `item_id` null = línea abierta / sin
 * ítem del menú: solo la deciden sus opciones.
 */
export function lineIsCouponAlcohol(
  line: { item_id: string | null | undefined; optionIds: readonly string[] },
  facts: CouponAlcoholFacts,
): boolean {
  if (facts.alcoholItemIds.length === 0 && facts.alcoholOptionIds.length === 0) return false
  const sets = alcoholSetsOf(facts)
  if (line.item_id != null && sets.items.has(line.item_id)) return true
  return line.optionIds.some((id) => sets.options.has(id))
}

// Sets construidos UNA vez por objeto de hechos (no `Array.includes` por línea):
// en el cliente esto corre en cada render del carrito, y en un súper el local
// puede tener cientos de ítems marcados. Cache por IDENTIDAD del objeto: los
// hechos se tratan como inmutables (el loader del servidor arma uno nuevo por
// consulta, EMPTY está congelado y el cupón del carrito se reemplaza entero al
// aplicarlo). WeakMap: un objeto que nadie referencia se va con su cache.
type AlcoholSets = { items: ReadonlySet<string>; options: ReadonlySet<string> }
const alcoholSetsCache = new WeakMap<CouponAlcoholFacts, AlcoholSets>()

function alcoholSetsOf(facts: CouponAlcoholFacts): AlcoholSets {
  let sets = alcoholSetsCache.get(facts)
  if (!sets) {
    sets = { items: new Set(facts.alcoholItemIds), options: new Set(facts.alcoholOptionIds) }
    alcoholSetsCache.set(facts, sets)
  }
  return sets
}

/**
 * Exclusiones del carrito del CLIENTE (caja, kiosko): qué líneas no toca el
 * cupón, por `cart_id`. R6: en A1b solo `couponCartIds` (A3 lo amplía).
 *
 * Va por `cart_id` y no por precio a propósito: `effectiveCartItems` (precio
 * único, redondeo de tarjeta) re-arma las líneas pero conserva el `cart_id`, así
 * que las exclusiones se calculan UNA vez sobre el carrito crudo y valen para
 * los lados efectivo y tarjeta.
 */
export type CouponDiscountExclusions = { couponCartIds: ReadonlySet<string> }

/**
 * Exclusiones para el cupón aplicado, sobre el carrito ACTUAL. Las listas son
 * del LOCAL ENTERO (las devuelve /api/coupons/apply), así que una cerveza
 * agregada después de aplicar el cupón también queda fuera.
 *
 * Devuelve `null` —el camino de main, byte a byte— sin cupón, sin listas
 * (bundle o respuesta viejos), con listas vacías (hoy, todos los locales) o
 * cuando ninguna línea del carrito es alcohol.
 */
export function couponExclusionsFor(
  items: ReadonlyArray<{
    cart_id: string
    item_id: string | null
    modifiers: ReadonlyArray<{ modifier_option_id: string }>
  }>,
  applied: { alcoholItemIds?: readonly string[]; alcoholOptionIds?: readonly string[] } | null | undefined,
): CouponDiscountExclusions | null {
  const facts = couponAlcoholFactsOf(applied)
  if (!facts) return null
  const couponCartIds = new Set<string>()
  for (const item of items) {
    if (lineIsCouponAlcohol({ item_id: item.item_id, optionIds: item.modifiers.map((m) => m.modifier_option_id) }, facts)) {
      couponCartIds.add(item.cart_id)
    }
  }
  return couponCartIds.size > 0 ? { couponCartIds } : null
}

/**
 * Hechos de alcohol de un cupón aplicado en el cliente, o `null` si no hay nada
 * que excluir. Si el cupón ya trae las dos listas se usa el MISMO objeto (así el
 * cache de Sets de arriba sirve entre renders); si trae una sola, se completa.
 */
export function couponAlcoholFactsOf(
  applied: { alcoholItemIds?: readonly string[]; alcoholOptionIds?: readonly string[] } | null | undefined,
): CouponAlcoholFacts | null {
  if (!applied) return null
  const itemIds = applied.alcoholItemIds ?? []
  const optionIds = applied.alcoholOptionIds ?? []
  if (itemIds.length === 0 && optionIds.length === 0) return null
  if (applied.alcoholItemIds && applied.alcoholOptionIds) return applied as CouponAlcoholFacts
  return { alcoholItemIds: itemIds, alcoholOptionIds: optionIds }
}

/**
 * Base del cupón: `rawSubtotal` menos las líneas de alcohol.
 *
 * Envuelve `discountBases` (sin no combinables: eso llega con A3) para que haya
 * UNA sola definición de la resta. Hereda su CAMINO RÁPIDO: sin líneas de
 * alcohol devuelve `rawSubtotal` intacto — el mismo número, sin re-sumar ni
 * redondear —, así que un local sin alcohol marcado recibe byte a byte lo de main.
 *
 * `excluded` = alguna línea de alcohol salió de la base: es la bandera con la que
 * la UI muestra «Cupón no aplica a productos con alcohol».
 *
 * `lineSubtotal` debe ser el aporte EXACTO de la línea a `rawSubtotal` (misma
 * expresión con la que el llamador sumó su subtotal).
 */
export function couponBaseFromLines(
  rawSubtotal: number,
  lines: ReadonlyArray<{ lineSubtotal: number; isAlcohol: boolean }>,
): { couponBase: number; excluded: boolean } {
  const excluded = lines.some((l) => l.isAlcohol)
  if (!excluded) return { couponBase: rawSubtotal, excluded: false }
  const { couponBase } = discountBases({
    rawSubtotal,
    lines: lines.map((l) => ({ lineSubtotal: l.lineSubtotal, nonCombinable: false, isAlcohol: l.isAlcohol })),
    excludeAlcoholFromCoupon: true,
  })
  // Tope: sacar líneas nunca puede SUBIR la base. Una línea de alcohol con
  // subtotal negativo (ajuste, devolución) la subiría por encima de rawSubtotal
  // y el cupón rebajaría más que sobre el carrito entero.
  return { couponBase: Math.min(couponBase, rawSubtotal), excluded: true }
}
